import { useConstructionPriceEstimatorState } from './useConstructionPriceEstimatorState';
import React, { useCallback, useRef } from 'react';
import { Alert, Box, Button, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import ConstructionPriceEstimatorTour from '../components/tour/ConstructionPriceEstimatorTour';
import WorkflowIntakePrefillNotice from '../components/HomeChat/WorkflowIntakePrefillNotice';
import {
  calculateConstructionPrices, cancelConstructionPriceSearchPrefillJob,
  cancelConstructionLaborPlanJob, cancelConstructionEstimateJob,
  prepareConstructionPriceSearchDetails, startConstructionLaborPlanJob,
  applyConstructionLaborPlan, removeConstructionEstimatorScopeDocument,
  uploadConstructionEstimatorScopeDocument, reviewConstructionSourceMatches,
  saveConstructionEstimateDraft, reviewConstructionDavisBaconSchedule,
  type EstimateDraft, type EstimateJob, type EstimateLoadData,
  type DavisBaconScheduleReviewDecision, type PriceSearchPrefillMode,
  type SourceMatchReviewDecision,
} from '../api/constructionPriceEstimatorFunctions';
import { appendWorkflowName, StageBackButton } from '../components/StageWrapper';
import { HistoricalAwardComparables } from './ConstructionPriceAwards';
import { ConstructionPriceContractDetails, ConstructionPriceLoadFailure,
  contractPrefillPresentation } from './ConstructionPriceContractDetails';
import { ConstructionPriceEstimatorScope } from './ConstructionPriceEstimatorScope';
import { emptyApproximateSettings, normalizeApproximateSettings } from '../api/approximateCostPlan';
import { hasGeneratedLaborPlan, laborPlanRolesReady } from './ApproximateCostSettings';
import { hasCompleteSelectedRecipeInputs, hasRequiredPriceSearchInputs, isValidPricingDate,
  rerunReminderMessage } from './ConstructionPriceEstimatorWorkflow';
import { downloadConstructionEstimate, DownloadPreconditionError, exportSavedConstructionEstimate, exportSavedConstructionEstimateDraft } from './ConstructionPriceEstimatorDownload';
import { WageDeterminationEvidence, WageDeterminationReview } from './ConstructionPriceWageEvidence';
import { ConstructionPriceCalculateControls, ConstructionPriceResults, hasConstructionPriceResultPrices } from './ConstructionPriceResults';
import { safeActionError } from './ConstructionPricePublicProjection';
import { useConstructionProjectBriefSave } from './useConstructionProjectBriefSave';
import { ACTIVE_JOB_STATUSES, draftContent, draftPayload, mergeUnsavedDraftFields,
  nestedObject } from './ConstructionPriceDraftState';
import { constructionSupportingEvidence, textList } from './ConstructionPriceSupportingEvidence';
import {
  btbPlanningBaselineForDisplay, constructionResultResearch,
  fallbackRomSourcesChecked, sourceLedgerForDisplay,
  sourcesCheckedWithAwardFailure, usaSpendingRetryMessage, usaspendingSourceRun,
} from './ConstructionPriceResearch';
import { StageTourButton } from '../components/StageTourButton';

interface ConstructionPriceEstimatorProps {
  flowId: string;
  onComplete?: () => void;
  pollIntervalMs?: number;
}


const ConstructionPriceEstimator: React.FC<ConstructionPriceEstimatorProps> = ({
  flowId,
  onComplete,
  pollIntervalMs = 1500,
}) => {
  const {
    flowName, isRom, romAckRequired, romAcknowledgementKey, requiredAcknowledgements, romFinalizationAllowed, canExport, canExportRevision, canExportSavedDraft, tourRef,
    load, setLoad, projectBriefText, setProjectBriefText, scopeDocumentBusy, setScopeDocumentBusy, laborPlanPreview,
    laborPlanProposal, setLaborPlanProposal, laborPlanJob, setLaborPlanJob, setLaborPlanPollAttempt, laborPlanReviewRoles, setLaborPlanReviewRoles, useSavedLaborPlan,
    setUseSavedLaborPlan, startingLaborPlan, setStartingLaborPlan, applyingLaborPlan, setApplyingLaborPlan, hasIncompleteCostComponentInputs, setHasIncompleteCostComponentInputs,
    rerunReminder, rerunToast, setRerunToast, draft, setDraft, revision, setRevision,
    job, setJob, loading, setLoading, saving, setSaving, calculating,
    setCalculating, preparingPrefill, setPreparingPrefill, prefillJob, setPrefillJob, setPrefillPollAttempt, cancellingPrefill,
    setCancellingPrefill, davisBaconDetails, setDavisBaconDetails, loadingDavisBaconDetails, setDavisBaconDetailsReload, davisBaconReviewReason, setDavisBaconReviewReason,
    submittingDavisBaconReview, setSubmittingDavisBaconReview, useSavedContractDetails, setUseSavedContractDetails, useSavedSourceResults, setUseSavedSourceResults, sourceMatchDecisions,
    setSourceMatchDecisions, reviewingSourceMatches, setReviewingSourceMatches, saveStatus, setSaveStatus, currentResultsState, setCurrentResultsState,
    calculationRunInProgress, setCalculationRunInProgress, calculationStage, setCalculationStage, setPollAttempt, finalizing, setFinalizing,
    error, setError, notice, setNotice, romAcknowledged, setRomAcknowledged, acknowledgedKeys,
    setAcknowledgedKeys, comparableRationales, setComparableRationales, cancelling, setCancelling, downloadInProgressRef, draftRef,
    acceptedDraftRef, partialDraftBaselineRef, acceptedPayloadRef, editSequenceRef, acceptedSaveSequenceRef, pendingSaveCountRef, saveQueueRef, saveFailedRef,
    calculationStartedAtRef, calculationRunInProgressRef, calculationHasTerminalWageEvidenceRef, prefillPendingRef, savedProjectBriefRef, projectBriefTextRef, flowGenerationRef,
    activeFlowIdRef, resultRequestGenerationRef, prefillRequestGenerationRef, laborRequestGenerationRef, scopeRequestGenerationRef, captureFlowFence, publishRerunReminder,
    applyLoad, editDraft, reload, settlePrefillJob, davisBaconScheduleJobIsActive, capabilities, primaryCostDataConfigured,
    primaryCostDataAvailability, canEdit, davisBaconCapability, davisBaconSchedule, blockers,
  } = useConstructionPriceEstimatorState(flowId, pollIntervalMs);
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  const persistDraftSnapshot = useCallback((reason = 'Autosaved project facts on blur.'): Promise<EstimateDraft | null> => {
    const generation = flowGenerationRef.current;
    const isCurrent = () => activeFlowIdRef.current === flowId && generation === flowGenerationRef.current;
    if (!isCurrent() || !canEditRef.current) return Promise.resolve(null);
    if (saveFailedRef.current) {
      saveQueueRef.current = Promise.resolve(acceptedDraftRef.current);
      saveFailedRef.current = false;
    }
    const snapshot = draftRef.current;
    const sequence = editSequenceRef.current;
    const payloadFingerprint = draftContent(snapshot);
    const previous = saveQueueRef.current;
    pendingSaveCountRef.current += 1;
    const task = previous.then(async () => {
      if (!isCurrent() || !canEditRef.current) return null;
      if ((acceptedDraftRef.current || partialDraftBaselineRef.current)
        && acceptedPayloadRef.current === payloadFingerprint) {
        return acceptedDraftRef.current ?? partialDraftBaselineRef.current;
      }
      setSaveStatus('saving');
      setError(null);
      const expectedRevision = acceptedDraftRef.current?.draft_revision
        ?? partialDraftBaselineRef.current?.draft_revision ?? snapshot.draft_revision;
      const saved = await saveConstructionEstimateDraft(
        flowId,
        draftPayload(reason, snapshot, snapshot.comparable_reviews, expectedRevision),
      );
      if (!isCurrent()) return null;
      const savedRecord = saved as EstimateLoadData;
      const returned = savedRecord?.capabilities
        ? savedRecord.draft ?? savedRecord.prefill?.partial_draft
        : (saved as EstimateDraft);
      const savedDraft = returned ? {
        ...snapshot, ...returned, construction_type: returned.construction_type ?? '',
        content_hash: returned.content_hash,
        project_location: { ...snapshot.project_location, ...returned.project_location },
      } as EstimateDraft : null;
      if (!savedDraft || savedDraft.draft_revision === undefined) {
        throw new Error('The server did not return the accepted project facts.');
      }
      const isPartial = savedRecord?.capabilities
        ? !savedRecord.draft : !savedDraft.construction_type.trim();
      acceptedDraftRef.current = isPartial ? null : savedDraft;
      partialDraftBaselineRef.current = isPartial ? savedDraft : null;
      acceptedPayloadRef.current = payloadFingerprint;
      acceptedSaveSequenceRef.current += 1;
      if (editSequenceRef.current === sequence) {
        const current = draftRef.current;
        const currentWithVersion = {
          ...current,
          draft_revision: savedDraft.draft_revision,
          content_hash: savedDraft.content_hash,
        };
        draftRef.current = currentWithVersion;
        setDraft(currentWithVersion);
      }
      setLoad((current) => current ? {
        ...current,
        draft: isPartial ? null : editSequenceRef.current === sequence
          ? { ...draftRef.current }
          : savedDraft,
        ...(isPartial ? { prefill: {
          ...(savedRecord.prefill ?? current.prefill), partial_draft: savedDraft,
        } } : {}),
      } : current);
      setSaveStatus('saved');
      return savedDraft;
    }).catch((caught) => {
      if (!isCurrent()) return null;
      saveFailedRef.current = true;
      setSaveStatus('error');
      setError(safeActionError(caught, 'Project facts could not be saved.'));
      throw caught;
    });
    saveQueueRef.current = task;
    void task.finally(() => {
      if (isCurrent()) pendingSaveCountRef.current -= 1;
    }).catch(() => undefined);
    void task.catch(() => undefined);
    return task;
  }, [draftContent, flowId]);

  const autosaveOnBlur = () => {
    if (!canEdit || prefillPendingRef.current) return;
    // A new standalone estimator intentionally begins without Contract
    // Details. Moving focus through that empty shell must not create an
    // invalid draft (and a misleading validation error) before the owner has
    // edited a contract field or accepted a server proposal.
    if (!acceptedDraftRef.current && editSequenceRef.current === 0) return;
    if (!acceptedDraftRef.current && !partialDraftBaselineRef.current
      && (!draftRef.current.construction_type.trim()
        || !isValidPricingDate(draftRef.current.estimate_date))) return;
    void persistDraftSnapshot().catch(() => undefined);
  };

  const setLoadAfterBriefSave = useCallback<React.Dispatch<React.SetStateAction<EstimateLoadData | null>>>((update) => {
    setLoad((current) => {
      const next = typeof update === 'function' ? update(current) : update;
      if (current && next && (next.project_brief?.revision !== current.project_brief?.revision
        || next.project_brief?.text !== current.project_brief?.text)) {
        return { ...next, prefill: null };
      }
      return next;
    });
  }, [setLoad]);

  const saveProjectBrief = useConstructionProjectBriefSave({
    flowId, canSave: canEdit && load?.standalone_mode === true,
    generation: flowGenerationRef, activeFlowId: activeFlowIdRef,
    text: projectBriefTextRef, saved: savedProjectBriefRef,
    setLoad: setLoadAfterBriefSave, setText: setProjectBriefText, setError,
  });

  const uploadScopeDocument = async (file: File | null) => {
    if (!file || !canEdit || !load?.standalone_mode || scopeDocumentBusy) return;
    const isCurrent = captureFlowFence();
    const requestGeneration = ++scopeRequestGenerationRef.current;
    setScopeDocumentBusy(true);
    setError(null);
    try {
      if (!canEditRef.current) return;
      const document = await uploadConstructionEstimatorScopeDocument(
        flowId, file, load.scope_document_revision ?? load.standalone_scope_document?.revision ?? 0,
      );
      if (!isCurrent() || requestGeneration !== scopeRequestGenerationRef.current) return;
      setLoad((current) => current ? { ...current, standalone_scope_document: document,
        scope_document_revision: document.revision, prefill: null } : current);
    } catch (caught) {
      if (isCurrent()) setError(safeActionError(caught, 'The SOW could not be uploaded.'));
    } finally {
      if (isCurrent() && requestGeneration === scopeRequestGenerationRef.current) setScopeDocumentBusy(false);
    }
  };

  const removeScopeDocument = async () => {
    const document = load?.standalone_scope_document;
    if (!document || !canEdit || scopeDocumentBusy) return;
    const isCurrent = captureFlowFence();
    const requestGeneration = ++scopeRequestGenerationRef.current;
    setScopeDocumentBusy(true);
    setError(null);
    try {
      if (!canEditRef.current) return;
      const documentRevision = await removeConstructionEstimatorScopeDocument(
        flowId, document.upload_id, load?.scope_document_revision ?? document.revision,
      );
      if (!isCurrent() || requestGeneration !== scopeRequestGenerationRef.current) return;
      setLoad((current) => current ? { ...current, standalone_scope_document: null,
        scope_document_revision: documentRevision, prefill: null } : current);
    } catch (caught) {
      if (isCurrent()) setError(safeActionError(caught, 'The SOW could not be removed.'));
    } finally {
      if (isCurrent() && requestGeneration === scopeRequestGenerationRef.current) setScopeDocumentBusy(false);
    }
  };

  const fillLaborPlan = async () => {
    if (!canEdit || !laborPlanPrerequisitesReady || startingLaborPlan || applyingLaborPlan
      || (laborPlanJob && ['pending', 'running'].includes(laborPlanJob.status))) return;
    const isCurrent = captureFlowFence();
    const requestGeneration = ++laborRequestGenerationRef.current;
    setStartingLaborPlan(true);
    setError(null);
    setNotice(null);
    setRerunToast(null);
    try {
      await saveProjectBrief();
      if (!isCurrent()) return;
      const accepted = await persistDraftSnapshot('Saved project facts before filling the labor plan.');
      if (!isCurrent()) return;
      if (!accepted?.content_hash) throw new Error('Save the contract details before filling the labor plan.');
      if (!canEditRef.current) return;
      const next = await startConstructionLaborPlanJob(
        flowId, accepted.draft_revision, accepted.content_hash, useSavedLaborPlan,
      );
      if (!isCurrent() || requestGeneration !== laborRequestGenerationRef.current) return;
      setLaborPlanJob(next);
      if (next.status === 'completed') {
        if (next.proposal) setLaborPlanProposal(next.proposal);
        else await reload();
      } else if (next.status === 'failed' || next.status === 'stale') {
        setError(safeActionError(next.failure_reason, 'The labor plan could not be filled.'));
      }
    } catch (caught) {
      if (isCurrent()) setError(safeActionError(caught, 'The labor plan could not be filled.'));
    } finally {
      if (isCurrent() && requestGeneration === laborRequestGenerationRef.current) setStartingLaborPlan(false);
    }
  };

  const cancelLaborPlan = async () => {
    if (!laborPlanJob || !['pending', 'running'].includes(laborPlanJob.status)) return;
    const isCurrent = captureFlowFence();
    const requestGeneration = ++laborRequestGenerationRef.current;
    try {
      if (!canEditRef.current) return;
      const next = await cancelConstructionLaborPlanJob(flowId, laborPlanJob.job_id);
      if (!isCurrent() || requestGeneration !== laborRequestGenerationRef.current) return;
      setLaborPlanJob(next);
      setNotice('Filling the labor plan was cancelled. Your saved work is unchanged.');
    } catch (caught) {
      if (isCurrent() && requestGeneration === laborRequestGenerationRef.current) {
        setError(safeActionError(caught, 'The labor plan could not be cancelled.'));
        setLaborPlanPollAttempt((attempt) => attempt + 1);
      }
    }
  };

  const excludeComparable = async (awardId: string) => {
    const rationale = comparableRationales[awardId]?.trim();
    if (!canEdit || saving || !rationale) return;
    const isCurrent = captureFlowFence();
    const comparableReviews = [
      ...(draftRef.current.comparable_reviews ?? []).filter((review) => review.award_id !== awardId),
      { award_id: awardId, included: false as const, rationale },
    ];
    const reason = `Excluded comparable ${awardId}: ${rationale}`.slice(0, 500);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      editDraft((current) => ({ ...current, comparable_reviews: comparableReviews }));
      const saved = await persistDraftSnapshot(reason);
      if (!isCurrent() || !saved) return;
      await reload();
      if (!isCurrent()) return;
      setComparableRationales((current) => ({ ...current, [awardId]: '' }));
      setNotice('Comparable exclusion saved. Recalculate to apply it to a new estimate revision.');
    } catch (caught) {
      if (!isCurrent()) return;
      setError(safeActionError(caught, 'The comparable review could not be saved.'));
    } finally {
      if (isCurrent()) setSaving(false);
    }
  };

  const calculatePrices = async () => {
    if (calculateActionDisabled || calculating
      || (load?.draft === null && editSequenceRef.current === 0)) return;
    const requestGeneration = ++resultRequestGenerationRef.current;
    setCalculating(true);
    calculationRunInProgressRef.current = true;
    calculationHasTerminalWageEvidenceRef.current = false;
    setCalculationRunInProgress(true);
    calculationStartedAtRef.current = Date.now();
    setCurrentResultsState('running');
    setCalculationStage('saving');
    setError(null);
    setNotice(null);
    setRerunToast(null);
    try {
      await saveProjectBrief();
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      let accepted = await persistDraftSnapshot('Saved project facts before calculating prices.');
      if (!accepted?.content_hash) {
        throw new Error('The accepted project facts are missing their validation hash. Save them and try again.');
      }
      if (laborPlanProposal?.status === 'proposed' && laborPlanReviewRoles) {
        setApplyingLaborPlan(true);
        if (!canEditRef.current) return;
        const applied = await applyConstructionLaborPlan(
          flowId, laborPlanProposal.proposal_id, accepted.draft_revision,
          accepted.content_hash, laborPlanReviewRoles,
        );
        if (requestGeneration !== resultRequestGenerationRef.current) return;
        accepted = applied.draft;
        acceptedDraftRef.current = accepted;
        acceptedPayloadRef.current = draftContent(accepted);
        draftRef.current = accepted;
        saveQueueRef.current = Promise.resolve(accepted);
        setDraft(accepted);
        setLaborPlanProposal(applied.proposal);
        setLaborPlanReviewRoles(null);
        setLoad((current) => current ? { ...current, draft: accepted,
          labor_plan_proposal: applied.proposal } : current);
      }
      if (!accepted.content_hash) throw new Error('The saved labor plan is missing its validation hash.');
      setCalculationStage('searching');
      // The unified route owns every lane server-side. The browser has one
      // fenced action and cannot choose a source, snapshot, price, or policy.
      if (!canEditRef.current) return;
      const nextJob = await calculateConstructionPrices(
        flowId,
        accepted.draft_revision,
        accepted.content_hash,
        !useSavedSourceResults,
      );
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      const startedJob = nestedObject<EstimateJob>(nextJob, 'job') ?? nextJob;
      setJob(startedJob);
      if (startedJob.status === 'complete' || startedJob.status === 'completed') {
        setCalculationStage('preparing');
        const loaded = await reload(true);
        if (requestGeneration !== resultRequestGenerationRef.current) return;
        if (!loaded) { setCurrentResultsState('unavailable'); return; }
        setNotice('Price-source calculation completed.');
      } else if (['canceled', 'cancelled'].includes(startedJob.status)) {
        setCurrentResultsState('cancelled');
      } else if (['failed', 'stale', 'blocked'].includes(startedJob.status)) {
        setCurrentResultsState('failed');
        setError(safeActionError(startedJob.message, 'The estimate calculation stopped before completion. Your saved draft is unchanged.'));
      }
    } catch (caught) {
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      saveQueueRef.current = Promise.resolve(acceptedDraftRef.current);
      calculationRunInProgressRef.current = false;
      setCalculationRunInProgress(false);
      setCurrentResultsState('failed');
      setError(safeActionError(caught, 'Prices could not be calculated.'));
    } finally {
      if (requestGeneration === resultRequestGenerationRef.current) {
        setCalculating(false);
        setApplyingLaborPlan(false);
      }
    }
  };

  const preparePriceSearchDetails = async (mode: PriceSearchPrefillMode) => {
    if (!canEdit || preparingPrefill || prefillPendingRef.current || activeFlowIdRef.current !== flowId) return;
    const requestGeneration = ++prefillRequestGenerationRef.current;
    prefillPendingRef.current = true;
    setPreparingPrefill(true);
    setError(null);
    setNotice(null);
    try {
      let expectedRevision = acceptedDraftRef.current?.draft_revision
        ?? partialDraftBaselineRef.current?.draft_revision ?? null;
      // Refresh preserves owner-owned fields on the server. Flush an edited
      // accepted draft (and any blur save already queued) before prefill starts
      // so its revision fence and owner provenance include the latest text.
      if (pendingSaveCountRef.current > 0 || ((acceptedDraftRef.current || partialDraftBaselineRef.current)
        && draftContent(draftRef.current) !== acceptedPayloadRef.current)) {
        const accepted = await persistDraftSnapshot('Saved owner edits before filling contract details.');
        expectedRevision = accepted?.draft_revision ?? expectedRevision;
      }
      if (requestGeneration !== prefillRequestGenerationRef.current) return;
      if (!canEditRef.current) return;
      const nextPrefillJob = await prepareConstructionPriceSearchDetails(
        flowId,
        expectedRevision,
        mode,
      );
      if (requestGeneration !== prefillRequestGenerationRef.current) return;
      setPrefillJob(nextPrefillJob);
      // The canonical endpoint returns 202. Keep the pending guard active
      // until polling observes a terminal server-owned job state.
      if (!['pending', 'running'].includes(nextPrefillJob.status)) {
        await settlePrefillJob(nextPrefillJob);
      }
    } catch (caught) {
      if (requestGeneration !== prefillRequestGenerationRef.current) return;
      prefillPendingRef.current = false;
      setPreparingPrefill(false);
      setError(safeActionError(caught, 'Contract details could not be filled. Your saved work is unchanged.'));
    }
  };

  const fillContractDetails = async () => {
    const hasStandaloneBrief = !load?.standalone_mode || projectBriefText.trim().length > 0
      || load?.standalone_scope_document?.extraction_status === 'ready';
    // For a standalone estimator, the Description is the authoritative
    // prefill input once this click saves it.  The capability is a load-time
    // advisory value and can be stale while the owner is typing, so it must
    // not prevent that save-then-fill action. Normal workflows still require
    // the server to advertise usable upstream construction context.
    const canStartPrefill = load?.standalone_mode ? hasStandaloneBrief : prefillAvailable;
    if (!canStartPrefill) return;
    // "Use saved work" means exactly that: retain the current, successful
    // server-owned fill. It is not a request to run the extractor again.
    // A stale/failed/no-result record is deliberately not reusable, and a
    // changed standalone Description invalidates the visible result before
    // its new text is saved.
    const standaloneDescriptionChanged = load?.standalone_mode === true
      && projectBriefText.trim() !== savedProjectBriefRef.current.text;
    if (load?.standalone_mode) {
      try {
        await saveProjectBrief();
      } catch {
        return;
      }
    }
    const latestPrefillFailed = ['failed', 'stale', 'cancelled'].includes(
      String(load?.prefill_job?.status ?? ''),
    );
    const hasCurrentUsableFill = Boolean(
      load?.draft
      && load?.prefill?.status === 'current'
      && ['proposed', 'prepared'].includes(String(load?.prefill?.outcome ?? ''))
      && !latestPrefillFailed
      && !standaloneDescriptionChanged,
    );
    if (useSavedContractDetails && hasCurrentUsableFill) return;

    if (!load?.draft) {
      void preparePriceSearchDetails('initial');
    } else if (useSavedContractDetails) {
      void preparePriceSearchDetails('refresh');
    } else {
      // An unchecked "Use saved work" is the owner's explicit choice to
      // replace generated details. The click that starts this action is the
      // confirmation; do not interrupt it with a second confirmation dialog.
      void preparePriceSearchDetails('replace_generated');
    }
  };

  const saveSourceMatchReview = async () => {
    const proposal = load?.source_match_proposal;
    if (!canEdit || reviewingSourceMatches || !proposal) return;
    const isCurrent = captureFlowFence();
    const decisions = proposal.candidates.flatMap((candidate): SourceMatchReviewDecision[] => {
      const decision = sourceMatchDecisions[candidate.proposal_id];
      return decision ? [{ proposal_id: candidate.proposal_id, decision }] : [];
    });
    if (decisions.length !== proposal.candidates.length) return;
    setReviewingSourceMatches(true);
    setError(null);
    setNotice(null);
    try {
      if (pendingSaveCountRef.current > 0
        || draftContent(draftRef.current) !== acceptedPayloadRef.current) {
        const accepted = await persistDraftSnapshot('Saved owner edits before reviewing technology matches.');
        if (!isCurrent() || !accepted) return;
      }
      const reviewBaseline = draftRef.current;
      if (!canEditRef.current) return;
      const saved = await reviewConstructionSourceMatches(flowId, proposal.proposal_revision, decisions);
      if (!isCurrent()) return;
      const savedLoad = saved as EstimateLoadData;
      if (savedLoad?.capabilities && savedLoad?.readiness) applyLoad(savedLoad, true, true);
      else {
        const savedDraft = saved as EstimateDraft;
        if (savedDraft?.draft_revision !== undefined) {
          acceptedDraftRef.current = savedDraft;
          acceptedPayloadRef.current = draftContent(savedDraft);
          acceptedSaveSequenceRef.current += 1;
          const presented = mergeUnsavedDraftFields(savedDraft, reviewBaseline, draftRef.current);
          draftRef.current = presented;
          setDraft(presented);
        }
      }
      setSourceMatchDecisions({});
      await reload();
      if (!isCurrent()) return;
      setNotice('Technology matches saved.');
    } catch (caught) {
      if (!isCurrent()) return;
      setError(safeActionError(caught, 'Technology matches could not be saved.'));
    } finally {
      if (isCurrent()) setReviewingSourceMatches(false);
    }
  };

  const cancelPriceSearchDetails = async () => {
    if (!canEdit || !prefillJob || !['pending', 'running'].includes(prefillJob.status) || cancellingPrefill) return;
    setCancellingPrefill(true);
    const requestGeneration = ++prefillRequestGenerationRef.current;
    setError(null);
    try {
      if (!canEditRef.current) return;
      const cancelled = await cancelConstructionPriceSearchPrefillJob(flowId, prefillJob.job_id);
      if (requestGeneration !== prefillRequestGenerationRef.current) return;
      setPrefillJob(cancelled);
      prefillPendingRef.current = false;
      setPreparingPrefill(false);
      setNotice('Filling contract details was cancelled.');
    } catch (caught) {
      if (requestGeneration !== prefillRequestGenerationRef.current) return;
      setError(safeActionError(caught, 'Filling contract details could not be cancelled.'));
      setPrefillPollAttempt((attempt) => attempt + 1);
    } finally {
      if (requestGeneration === prefillRequestGenerationRef.current) setCancellingPrefill(false);
    }
  };

  const submitDavisBaconReview = async (decision: DavisBaconScheduleReviewDecision) => {
    const scheduleId = davisBaconDetails?.schedule_id;
    const token = davisBaconDetails?.review_token;
    const reason = davisBaconReviewReason.trim();
    if (!canEdit || davisBaconCapability?.can_review !== true || !scheduleId || !token || !reason || submittingDavisBaconReview) return;
    const isCurrent = captureFlowFence();
    setSubmittingDavisBaconReview(true);
    setError(null);
    try {
      if (!canEditRef.current) return;
      await reviewConstructionDavisBaconSchedule(flowId, scheduleId, token, decision, reason);
      if (!isCurrent()) return;
      setDavisBaconReviewReason('');
      setDavisBaconDetails(null);
      setDavisBaconDetailsReload((current) => current + 1);
      await reload();
      if (!isCurrent()) return;
      setNotice(decision === 'approve' ? 'The wage determination was approved.' : 'The wage determination was rejected.');
    } catch (caught) {
      if (!isCurrent()) return;
      if ((caught as { error_code?: string })?.error_code === 'davis_bacon_schedule_review_conflict') {
        setNotice('This wage-schedule review changed. The latest schedule has been reloaded.');
        setDavisBaconDetails(null);
        setDavisBaconDetailsReload((current) => current + 1);
        await reload();
      } else {
        setError('The wage-schedule review could not be saved. The schedule remains unchanged.');
      }
    } finally {
      if (isCurrent()) setSubmittingDavisBaconReview(false);
    }
  };

  const cancelCalculation = async () => {
    const jobCanCancel = job?.capabilities?.can_cancel ?? capabilities?.can_cancel;
    if (!job || !canEdit || jobCanCancel !== true || !ACTIVE_JOB_STATUSES.has(job.status) || cancelling) return;
    setCancelling(true);
    const requestGeneration = ++resultRequestGenerationRef.current;
    setError(null);
    try {
      if (!canEditRef.current) return;
      const cancelled = await cancelConstructionEstimateJob(flowId, job.job_id, 'user_cancelled_from_estimator_ui');
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      setJob(cancelled);
      setCurrentResultsState('cancelled');
      setNotice(null);
    } catch (caught) {
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      setError(safeActionError(caught, 'The estimate calculation could not be cancelled.'));
      setPollAttempt((attempt) => attempt + 1);
    } finally {
      if (requestGeneration === resultRequestGenerationRef.current) setCancelling(false);
    }
  };

  const downloadEstimate = async () => {
    if ((!canEdit && !canExport) || downloadInProgressRef.current) return;
    const isCurrentFlow = captureFlowFence();
    downloadInProgressRef.current = true;
    const requestGeneration = resultRequestGenerationRef.current;
    setFinalizing(true);
    setError(null);
    try {
      if (!canEdit) {
        if (!canExport) return;
        const isCurrent = () => isCurrentFlow() && requestGeneration === resultRequestGenerationRef.current;
        const exported = canExportRevision && revision
          ? await exportSavedConstructionEstimate(flowId, revision.revision, isCurrent)
          : canExportSavedDraft && load?.draft
            ? await exportSavedConstructionEstimateDraft(flowId, load.draft, isCurrent) : false;
        if (exported) setNotice('Saved estimate downloaded.');
        return;
      }
      await saveProjectBrief();
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      const accepted = await persistDraftSnapshot('Saved project facts before downloading estimate.');
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      if (!accepted) throw new DownloadPreconditionError('Save the contract details before downloading an estimate.');
      const outcome = await downloadConstructionEstimate({
        flowId, accepted, revision, capabilities, romFinalizationAllowed,
        allAcknowledged: requiredAcknowledgements.every((key) => acknowledgedKeys.has(key)),
        romAckRequired, romAcknowledged, requiredAcknowledgements, romAcknowledgementKey,
        isCurrent: () => requestGeneration === resultRequestGenerationRef.current
          && activeFlowIdRef.current === flowId,
      });
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      if (!outcome) return;
      if (outcome.revision) setRevision(outcome.revision);
      setNotice(outcome.draftOnly
        ? 'Saved and downloaded a draft without calculated prices. Calculate prices to include an estimate.'
        : 'Estimate finalized and downloaded. This result is an internal planning revision, not an approved Independent Government Estimate.');
      if (!outcome.draftOnly) onComplete?.();
    } catch (caught) {
      if (requestGeneration !== resultRequestGenerationRef.current) return;
      setError(caught instanceof DownloadPreconditionError
        ? caught.message : safeActionError(caught, 'The estimate could not be saved and downloaded.'));
    } finally {
      if (isCurrentFlow()) {
        downloadInProgressRef.current = false;
        setFinalizing(false);
      }
    }
  };

  const { researchSnapshot, unifiedSourceResearch, primaryApproximateCostPlan,
    sourceEvidenceRuns, canonicalAwards, isolatedSourceLedger } =
    constructionResultResearch(load, revision, job);
  const calculationBusy = calculating || Boolean(job && ACTIVE_JOB_STATUSES.has(job.status));
  const wageDeterminationBlocksCurrentResult = davisBaconScheduleJobIsActive
    && (Boolean(revision) || currentResultsState === 'running' || calculationBusy || calculationRunInProgress);
  const sourceSearchBusy = currentResultsState === 'running' || calculationBusy
    || calculationRunInProgress || wageDeterminationBlocksCurrentResult;
  const sourceSearchFailed = currentResultsState === 'failed' || currentResultsState === 'unavailable';
  const sourceSearchCancelled = currentResultsState === 'cancelled';
  const jobCanCancel = job?.capabilities?.can_cancel ?? capabilities?.can_cancel;
  const showCancel = Boolean(job && jobCanCancel === true && ACTIVE_JOB_STATUSES.has(job.status));
  const hasCurrentResult = Boolean(unifiedSourceResearch || researchSnapshot || revision);
  const btbPlanningBaseline = btbPlanningBaselineForDisplay(revision?.btb_planning_baseline);
  const btbBreakdown = unifiedSourceResearch?.btb_breakdown ?? null;
  const georgiaObservedEvidence = unifiedSourceResearch?.georgia_observed_evidence ?? null;
  const currentComparables = (
    (researchSnapshot?.comparables && typeof researchSnapshot.comparables === 'object'
      ? researchSnapshot.comparables
      : revision?.comparables) ?? {}
  ) as Record<string, any>;
  const legacyManualOnly = Boolean(
    revision
    && !researchSnapshot
    && canonicalAwards.length === 0
    && sourceEvidenceRuns.some((run) => run.source === 'manual_allowance' || run.source === 'user_allowance'),
  );
  const { prefillAvailable, fillContractDetailsAvailable, showPrefillEntry } =
    contractPrefillPresentation(load, projectBriefText);
  const supportedCostRecipes = capabilities?.approximate_cost_plan?.supported_recipes ?? [];
  const proposedLaborRoles = laborPlanProposal?.status === 'proposed' ? laborPlanReviewRoles : null;
  const pricingInputDraft = proposedLaborRoles ? {
    ...draft, approximate_cost_plan_settings: {
      ...(normalizeApproximateSettings(draft.approximate_cost_plan_settings) ?? emptyApproximateSettings()),
      site_staffing_roles: proposedLaborRoles,
    },
  } : draft;
  const proposedRolesReady = proposedLaborRoles !== null
    && laborPlanRolesReady(proposedLaborRoles);
  const requiredPriceSearchInputsComplete = hasRequiredPriceSearchInputs(
    pricingInputDraft, supportedCostRecipes, proposedRolesReady,
  )
    && (laborPlanProposal?.status !== 'proposed'
      || proposedRolesReady);
  const takeoffSettings = normalizeApproximateSettings(draft.approximate_cost_plan_settings) ?? emptyApproximateSettings();
  const laborPlanGenerated = hasGeneratedLaborPlan(takeoffSettings, laborPlanProposal);
  const hasSelectedCostComponents = takeoffSettings.material_components.length
    + takeoffSettings.assembly_components.length
    + takeoffSettings.project_work_requirements.length > 0;
  // Manual staffing edits clear generation provenance, but an existing
  // estimate still supports the elastic price-only recalculation workflow.
  const needsFirstLaborPlan = hasSelectedCostComponents && !laborPlanGenerated && !revision;
  const laborPlanPrerequisitesReady = Boolean(draft.construction_type.trim()
    && draft.davis_bacon_construction_type) && isValidPricingDate(draft.estimate_date)
    && Boolean(draft.project_location.state?.trim()
    && draft.project_location.county?.trim())
    && !hasIncompleteCostComponentInputs
    && hasCompleteSelectedRecipeInputs(draft.approximate_cost_plan_settings, supportedCostRecipes)
    && hasSelectedCostComponents;
  const aiPlanningEstimateCount = takeoffSettings.component_takeoff.filter((item) => (
    item.lifecycle === 'accepted' && item.quantity_provenance === 'ai_planning_estimate'
  )).length;
  const calculateActionDisabled = !canEdit || capabilities?.can_calculate === false
    || sourceSearchBusy || preparingPrefill || startingLaborPlan || applyingLaborPlan
    || Boolean(laborPlanJob && ['pending', 'running'].includes(laborPlanJob.status))
    || !requiredPriceSearchInputsComplete || hasIncompleteCostComponentInputs
    || needsFirstLaborPlan
    || (load?.draft === null && editSequenceRef.current === 0);
  const costDataSummary = revision?.cost_data_summary ?? load?.cost_data_summary ?? null;
  const displayedCostDataSummary = costDataSummary;

  // A primary snapshot result does not imply that USAspending was queried.
  // Surface the distinct awards panel only when that separate context lane
  // actually has a snapshot/rows, or for the legacy award-only result.
  const awardLaneCompleted = Boolean(unifiedSourceResearch?.lanes?.some((lane) =>
    lane.label === 'Previous federal awards'));
  const showPreviousAwards = !primaryCostDataConfigured || isRom || Boolean(researchSnapshot)
    || canonicalAwards.length > 0 || awardLaneCompleted;
  const awardEvidenceIsContextOnly = canonicalAwards.length > 0
    && (unifiedSourceResearch?.recommendation?.outcome === 'context_only'
      || revision?.unified_source_research?.recommendation?.outcome === 'context_only')
    && primaryApproximateCostPlan.award_reference?.compatible !== true;
  const previousAwardsPanel = hasCurrentResult && !legacyManualOnly && showPreviousAwards ? (
    <HistoricalAwardComparables
      items={canonicalAwards}
      sourceRuns={sourceEvidenceRuns}
      comparables={currentComparables}
      warnings={textList(researchSnapshot?.warnings ?? currentComparables.warnings ?? revision?.warnings)}
      isRom={isRom}
      canEdit={canEdit}
      saving={saving}
      rationales={comparableRationales}
      onRationaleChange={(awardId, value) => setComparableRationales((current) => ({ ...current, [awardId]: value }))}
      onExclude={(awardId) => void excludeComparable(awardId)}
      comparableCohort={unifiedSourceResearch?.comparable_cohort}
      awardEvidenceIsContextOnly={awardEvidenceIsContextOnly}
      embedded
    />
  ) : null;
  const wageDeterminationReview = davisBaconDetails?.review_state === 'review_required' && canEdit && davisBaconCapability?.can_review === true ? (
    <WageDeterminationReview reason={davisBaconReviewReason}
      onReasonChange={setDavisBaconReviewReason}
      submitting={submittingDavisBaconReview}
      onDecision={(decision) => void submitDavisBaconReview(decision)} />
  ) : null;
  const wageLaneCompleted = Boolean(unifiedSourceResearch?.lanes?.some((lane) =>
    lane.label === 'Wage determination'));
  const wageDeterminationEvidence = hasCurrentResult && (
    davisBaconSchedule || davisBaconDetails || loadingDavisBaconDetails
    || primaryApproximateCostPlan.labor_coverage?.reason || wageLaneCompleted
  ) ? <WageDeterminationEvidence
    schedule={davisBaconSchedule}
    details={davisBaconDetails}
    loading={loadingDavisBaconDetails}
    unavailableReason={primaryApproximateCostPlan.labor_coverage?.reason}
    review={wageDeterminationReview}
  /> : undefined;
  const sourceLedger = unifiedSourceResearch?.sources_checked?.length
    ? unifiedSourceResearch.sources_checked
    : isolatedSourceLedger.length ? isolatedSourceLedger
    : primaryApproximateCostPlan.sources_checked?.length
      ? primaryApproximateCostPlan.sources_checked
    : fallbackRomSourcesChecked(unifiedSourceResearch, primaryApproximateCostPlan, davisBaconSchedule);
  const awardSourceRetryMessage = usaSpendingRetryMessage(usaspendingSourceRun(sourceEvidenceRuns));
  const sourcesChecked = sourceLedgerForDisplay(sourcesCheckedWithAwardFailure(sourceLedger, sourceEvidenceRuns));
  const wageRefreshFailedWithSavedSchedule = revision?.source_phases?.['Wage determination'] === 'unavailable'
    && (load?.davis_bacon_schedule_job?.saved_schedule_reused === true
      || sourcesChecked.some((source) => source.outcome === 'reused' && /wage determination/i.test(source.label)));
  const supportingEvidence = constructionSupportingEvidence({
    revision, summary: displayedCostDataSummary, baseline: btbPlanningBaseline,
    breakdown: btbBreakdown, georgia: georgiaObservedEvidence,
    state: draft.project_location.state ?? undefined,
  });

  if (loading) return <Stack direction="row" spacing={1.5} alignItems="center" aria-label="Loading price estimator"><CircularProgress size={24} aria-label="Loading saved estimate" /><Typography>Loading saved estimate…</Typography></Stack>;
  if (!load) return <ConstructionPriceLoadFailure error={error} onRetry={() => { setLoading(true); void reload(); }} />;
  if (!capabilities?.applicable) return <Alert severity="info">Price Estimator is not part of this workflow's persisted path. Legacy workflows remain complete at Vendor Discovery.</Alert>;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Typography data-tour-id="price-title" variant="h4" component="h1">{appendWorkflowName('Price realism estimator', flowName)}</Typography>
        <StageTourButton onClick={() => tourRef.current?.replay()} />
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Calculate prices checks approved source evidence and returns one planning recommendation when compatible direct pricing is available. Previous federal awards remain context, not a unit-price estimate.
      </Alert>
      {primaryCostDataAvailability && <Alert severity="info" sx={{ mb: 2 }}>{primaryCostDataAvailability}</Alert>}
      {load?.draft === null && !prefillAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No project estimate draft has been saved yet. Enter project facts and scope below, then save the draft before calculation.
        </Alert>
      )}
      {!capabilities.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>Calculation is currently disabled by the server. Saved drafts and revisions remain available.</Alert>
      )}
      {error && !sourceSearchFailed && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Snackbar key={rerunToast ?? 'closed'} open={rerunToast !== null} autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setRerunToast(null)}>
        <Alert severity="error" role="status" aria-label="Rerun reminder notification"
          onClose={() => setRerunToast(null)}>{rerunToast ?? ''}</Alert>
      </Snackbar>

      {load?.standalone_mode && <WorkflowIntakePrefillNotice prefill={load.intake_prefill} />}
      {load?.standalone_mode && <ConstructionPriceEstimatorScope flowId={flowId} value={projectBriefText}
        document={load.standalone_scope_document} canEdit={canEdit && !sourceSearchBusy} busy={scopeDocumentBusy}
        onChange={(value) => {
          projectBriefTextRef.current = value;
          setProjectBriefText(value);
        }} onBlur={() => { void saveProjectBrief().catch(() => undefined); }}
        onUpload={(file) => void uploadScopeDocument(file)}
        onRemove={() => void removeScopeDocument()} />}

      <ConstructionPriceContractDetails
        load={load}
        draft={draft}
        capabilities={capabilities}
        blockers={blockers}
        canEdit={canEdit && !sourceSearchBusy}
        preparingPrefill={preparingPrefill}
        fillContractDetailsAvailable={fillContractDetailsAvailable}
        showPrefillEntry={showPrefillEntry}
        useSavedContractDetails={useSavedContractDetails}
        setUseSavedContractDetails={setUseSavedContractDetails}
        prefillJob={prefillJob}
        cancellingPrefill={cancellingPrefill}
        sourceMatchDecisions={sourceMatchDecisions}
        setSourceMatchDecisions={setSourceMatchDecisions}
        reviewingSourceMatches={reviewingSourceMatches}
        sourceSearchBusy={sourceSearchBusy}
        fillContractDetails={fillContractDetails}
        cancelPriceSearchDetails={cancelPriceSearchDetails}
        saveSourceMatchReview={saveSourceMatchReview}
        editDraft={editDraft}
        updateLocation={(key, value) => editDraft((current) => ({
          ...current,
          project_location: { ...current.project_location, [key]: value },
        }))}
        autosaveOnBlur={autosaveOnBlur}
        flowId={flowId}
        onIncompleteComponentChange={setHasIncompleteCostComponentInputs}
        onSettingsChange={(value) => {
          editDraft((current) => ({ ...current, approximate_cost_plan_settings: value }));
          if (laborPlanProposal?.status === 'proposed') setLaborPlanProposal({ ...laborPlanProposal, status: 'stale' });
        }}
        laborPlan={{
          prerequisitesReady: laborPlanPrerequisitesReady,
          rerunReminder: revision && rerunReminder?.level === 'materials' && laborPlanPrerequisitesReady
            ? rerunReminderMessage('materials') : null,
          preview: laborPlanPreview, proposal: laborPlanProposal, reviewRoles: laborPlanReviewRoles,
          onReviewRolesChange: setLaborPlanReviewRoles,
          busy: startingLaborPlan || Boolean(laborPlanJob && ['pending', 'running'].includes(laborPlanJob.status)),
          applying: applyingLaborPlan, useSavedWork: useSavedLaborPlan,
          onUseSavedWorkChange: setUseSavedLaborPlan,
          onFill: () => void fillLaborPlan(), onCancel: () => void cancelLaborPlan(),
        }}
      />
      {!showPrefillEntry && needsFirstLaborPlan && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fill the labor plan before calculating prices for the selected components.
        </Alert>
      )}
      <ConstructionPriceCalculateControls visible canEdit={canEdit}
        busy={sourceSearchBusy} preparingPrefill={preparingPrefill}
        useSavedSourceResults={useSavedSourceResults}
        setUseSavedSourceResults={setUseSavedSourceResults}
        primaryCostDataConfigured={primaryCostDataConfigured}
        saveStatus={saveStatus} onCalculate={() => void calculatePrices()}
        disabled={calculateActionDisabled} aiPlanningEstimateCount={aiPlanningEstimateCount}
        rerunReminder={revision && rerunReminder
          && (rerunReminder.level === 'prices' || !laborPlanPrerequisitesReady)
          ? rerunReminderMessage(rerunReminder.level, laborPlanPrerequisitesReady) : null} />

      <ConstructionPriceResults timingContext={{ draft, prefill: load?.prefill ?? null }}
        sourceSearchBusy={sourceSearchBusy}
        sourceSearchFailed={sourceSearchFailed}
        resultLoadUnavailable={currentResultsState === 'unavailable'}
        sourceSearchCancelled={sourceSearchCancelled}
        calculationStage={calculationStage}
        job={job}
        showCancel={showCancel}
        canEdit={canEdit}
        canDownload={canEdit || canExport}
        cancelling={cancelling}
        cancelCalculation={cancelCalculation}
        error={error}
        showPrefillEntry={showPrefillEntry}
        hasCurrentResult={hasCurrentResult}
        legacyManualOnly={legacyManualOnly}
        primaryApproximateCostPlan={primaryApproximateCostPlan}
        publicPriceEvidence={unifiedSourceResearch?.public_price_evidence}
        wageRefreshFailed={wageRefreshFailedWithSavedSchedule}
        awardEvidenceIsContextOnly={awardEvidenceIsContextOnly}
        previousAwardsPanel={previousAwardsPanel}
        wageDeterminationEvidence={wageDeterminationEvidence}
        sourcesChecked={sourcesChecked}
        awardSourceRetryMessage={awardSourceRetryMessage}
        supportingEvidence={supportingEvidence}
        displayedCostDataSummary={displayedCostDataSummary}
        revision={revision}
        requiredAcknowledgements={requiredAcknowledgements}
        acknowledgedKeys={acknowledgedKeys}
        setAcknowledgedKeys={setAcknowledgedKeys}
        romAckRequired={romAckRequired}
        romAcknowledged={romAcknowledged}
        setRomAcknowledged={setRomAcknowledged}
        isRom={isRom}
        romFinalizationAllowed={romFinalizationAllowed}
        finalizing={finalizing}
        downloadEstimate={downloadEstimate}
        capabilities={capabilities}
      />
      <StageBackButton />
      <ConstructionPriceEstimatorTour ref={tourRef} standalone={load.standalone_mode === true}
        canEdit={canEdit} detailsReady={!showPrefillEntry && !preparingPrefill}
        resultsReady={!sourceSearchBusy && !sourceSearchFailed && !sourceSearchCancelled && hasCurrentResult}
        downloadReady={hasConstructionPriceResultPrices(primaryApproximateCostPlan, awardEvidenceIsContextOnly, isRom, revision)}
        busy={preparingPrefill || sourceSearchBusy || startingLaborPlan
          || Boolean(laborPlanJob && ['pending', 'running'].includes(laborPlanJob.status))} />
    </Box>
  );
};

export default ConstructionPriceEstimator;
